import { Injectable } from '@angular/core';
import { LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { Mimic } from 'icreate/common/models/mimic';
import { MimicFolderViewData, RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { Vessel } from 'icreate/common/models/vessel';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { LibrarySocketService } from './library-socket.service';
import { LibraryCategorySocketService } from './library-category-socket.service';
import { MimicFolderSocketService } from './mimic-folder-socket.service';
import { MimicSocketService } from './mimics-socket.service';
import { EditorService } from './editor.service';

@Injectable({ providedIn: 'root' })
export class EditorSocketHelperService {
    private destroySubscriptionSubject$ = new Subject<void>();

    private vessel?: Vessel;

    private rootList: RootItem[] = [];
    private libraryList: LibraryAndCategories[] = [];
    private selectedMimic?: Mimic;

    private readonly rootListSubject = new BehaviorSubject<RootItem[]>([]);
    private readonly libraryListSubject = new BehaviorSubject<LibraryAndCategories[]>([]);

    readonly rootList$ = this.rootListSubject.asObservable();
    readonly libraryList$ = this.libraryListSubject.asObservable();

    constructor(
        private librarySocketService: LibrarySocketService,
        private libraryCategorySocketService: LibraryCategorySocketService,
        private mimicFolderSocketService: MimicFolderSocketService,
        private mimicSocketService: MimicSocketService,
        private editorService: EditorService
    ) {}

    init(params: {
        vessel: Vessel;
        initialRootList: RootItem[];
        initialLibraryList: LibraryAndCategories[];
        initialSelectedMimic?: Mimic;
    }) {
        this.vessel = params.vessel;

        this.rootList = params.initialRootList ?? [];
        this.libraryList = params.initialLibraryList ?? [];
        this.selectedMimic = params.initialSelectedMimic;

        this.rootListSubject.next([...this.rootList]);
        this.libraryListSubject.next([...this.libraryList]);

        this.bindSockets();
    }

    setSelectedMimic(m: Mimic | undefined) {
        this.selectedMimic = m;
    }

    setRootList(rootList: RootItem[]) {
        this.rootList = [...rootList];
    }

    private bindSockets() {
        this.destroySubscriptionSubject$ = new Subject();

        this.librarySocketService
            .libraryCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newLibrary: LibraryAndCategories) => {
                if (newLibrary.shipyardId !== this.vessel.shipyardId) return;
                this.libraryList = [...this.libraryList, { ...newLibrary, expanded: false }];
                this.libraryListSubject.next(this.libraryList);
            });

        this.librarySocketService
            .libraryUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedLibrary: LibraryAndCategories) => {
                if (updatedLibrary.shipyardId !== this.vessel.shipyardId) return;
                this.libraryList = this.libraryList.map(lib =>
                    lib.id === updatedLibrary.id ? { ...updatedLibrary, expanded: false } : lib
                );
                this.libraryListSubject.next(this.libraryList);
            });

        this.librarySocketService
            .libraryDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((libraryId: string) => {
                const deletedLibraryIndex = this.libraryList.findIndex(lib => lib.id !== libraryId);
                if (deletedLibraryIndex < 0) return;
                this.libraryList = this.libraryList.filter(lib => lib.id !== libraryId);
                this.libraryListSubject.next(this.libraryList);
            });

        this.libraryCategorySocketService
            .libraryCategoryCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newCategory: LibraryCategory) => {
                if (newCategory.shipyardId !== this.vessel.shipyardId) return;
                this.libraryList = this.libraryList.map(lib =>
                    lib.id === newCategory.libraryId
                        ? {
                              ...lib,
                              libraryCategories: [...lib.libraryCategories, newCategory],
                              expanded: true,
                          }
                        : lib
                );
                this.libraryListSubject.next(this.libraryList);
            });

        this.libraryCategorySocketService
            .libraryCategoryUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedCategory: LibraryCategory) => {
                if (updatedCategory.shipyardId !== this.vessel.shipyardId) return;
                this.libraryList = this.libraryList.map(lib =>
                    lib.id !== updatedCategory.libraryId
                        ? lib
                        : {
                              ...lib,
                              libraryCategories: lib.libraryCategories.map(cat =>
                                  cat.id === updatedCategory.id ? updatedCategory : cat
                              ),
                              expanded: true,
                          }
                );
                this.libraryListSubject.next(this.libraryList);
            });

        this.libraryCategorySocketService
            .libraryCategoryDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((categoryId: string) => {
                this.libraryList = this.libraryList.map(lib => {
                    const libraryCatIndex = lib.libraryCategories.findIndex(cat => cat.id == categoryId);
                    if (libraryCatIndex < 0) return lib;
                    lib.libraryCategories.splice(libraryCatIndex, 1);
                    return lib;
                });
                this.libraryListSubject.next(this.libraryList);
            });

        this.mimicFolderSocketService
            .mimicFolderCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newFolder: MimicFolderViewData) => {
                if (newFolder.vesselId !== this.vessel.id) return;
                this.rootList = this.rootList.concat({
                    type: RootItemType.folder,
                    index: this.rootList.length,
                    vesselId: this.vessel.id,
                    folder: { ...newFolder, expanded: false },
                });
                this.rootListSubject.next([...this.rootList]);
            });

        this.mimicFolderSocketService
            .mimicFolderUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(this.syncRootList);

        this.mimicFolderSocketService
            .mimicFolderDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ deletedRootList, vesselId }: { deletedRootList: RootItem[]; vesselId: string }) => {
                if (vesselId !== this.vessel.id) return;
                if (!deletedRootList?.length) {
                    this.rootListSubject.next([]);
                    this.editorService.setCurrentMimic(undefined);
                    return;
                }

                this.rootList = this.applyExpandedState(deletedRootList, this.rootList);

                const selectedId = this.selectedMimic?.id;
                if (selectedId) {
                    const { nextSelected } = this.findMimicInRootList(this.rootList, selectedId);
                    if (!nextSelected) {
                        const { nextMimic, rootList: updatedRootList } = this.findCurrentMimic(this.rootList);

                        this.rootListSubject.next([...updatedRootList]);
                        this.editorService.setCurrentMimic(nextMimic);
                    } else {
                        this.rootListSubject.next([...this.rootList]);
                    }
                }
            });

        this.mimicFolderSocketService
            .mimicFolderUpdatedIndex$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(this.syncRootList);

        this.mimicSocketService
            .mimicCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newMimic: Mimic) => {
                if (newMimic.vesselId !== this.vessel.id) return;

                this.rootList = this.mimicCreated(this.rootList, newMimic);
                this.rootListSubject.next([...this.rootList]);

                if (!this.selectedMimic) {
                    this.selectedMimic = newMimic;
                    this.editorService.setCurrentMimic(newMimic);
                }
            });

        this.mimicSocketService
            .mimicUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedRootList: RootItem[]) => {
                if (updatedRootList.length == 0) return;
                if (updatedRootList[0].vesselId !== this.vessel.id) return;
                const { rootList, currentMimic } = this.mimicUpdated(
                    this.rootList,
                    updatedRootList,
                    this.selectedMimic
                );
                this.rootList = rootList;
                this.rootListSubject.next([...this.rootList]);
                this.editorService.setCurrentMimic(currentMimic);
            });

        this.mimicSocketService
            .mimicDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ mimicId, folderId, vesselId }) => {
                if (vesselId !== this.vessel.id) return;
                const { rootList, currentMimic } = this.mimicDeleted(
                    this.rootList,
                    mimicId,
                    folderId,
                    this.selectedMimic
                );
                this.rootList = rootList;
                this.rootListSubject.next([...this.rootList]);
                this.editorService.setCurrentMimic(currentMimic);
            });

        this.mimicSocketService
            .mimicUpdatedIndex$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(this.syncRootList);
    }

    destroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    syncRootList = (incoming: RootItem[]) => {
        if (!incoming?.length || incoming[0].vesselId !== this.vessel.id) return;
        this.rootList = this.applyExpandedState(incoming, this.rootList);
        this.rootListSubject.next([...this.rootList]);
    };

    mimicFolderCreated(rootList: RootItem[], newFolder: MimicFolderViewData, vessel: Vessel): RootItem[] {
        return rootList.concat({
            type: RootItemType.folder,
            index: rootList.length,
            vesselId: vessel.id,
            folder: newFolder,
        });
    }

    rootListChange(rootList: RootItem[], nextRootList: RootItem[]) {
        const changedRootList = this.applyExpandedState(nextRootList, rootList);
        return changedRootList;
    }

    mimicCreated(rootList: RootItem[], newMimic: Mimic) {
        const addToIndex = rootList.findIndex(rootItem => rootItem?.folder?.id === newMimic.folderId);
        if (addToIndex !== -1) {
            const target = rootList[addToIndex];

            const nextMimicList = [...(target.folder.mimicList ?? []), newMimic];

            const nextRootItem = {
                ...target,
                folder: {
                    ...target.folder,
                    mimicList: nextMimicList,
                    expanded: true,
                },
            };

            return rootList.map((item, idx) => (idx === addToIndex ? nextRootItem : item));
        } else {
            return rootList.concat({
                type: RootItemType.mimic,
                vesselId: newMimic.vesselId,
                index: rootList.length,
                mimic: newMimic,
            });
        }
    }

    mimicUpdated(rootList: RootItem[], updatedRootList: RootItem[], selectedMimic: Mimic) {
        const selectedId = selectedMimic?.id;

        const nextRootList = this.applyExpandedState(updatedRootList, rootList);
        rootList = [...nextRootList];

        let currentMimic: Mimic = undefined;

        if (selectedId) {
            const { nextSelected, folderIndex } = this.findMimicInRootList(rootList, selectedId);
            if (folderIndex !== undefined) {
                rootList[folderIndex].folder.expanded = true;
            }
            currentMimic = nextSelected;
        } else {
            const { nextMimic, rootList: updatedRootList } = this.findCurrentMimic(rootList);
            currentMimic = nextMimic;
            rootList = updatedRootList;
        }

        return { rootList: [...rootList], currentMimic };
    }

    mimicDeleted(rootList: RootItem[], mimicId: string, folderId: string, selectedMimic: Mimic) {
        if (folderId) {
            const folderIndex = rootList.findIndex(
                item => item.type === RootItemType.folder && item?.folder?.id === folderId
            );

            if (folderIndex !== -1) {
                const list = rootList[folderIndex].folder.mimicList;
                const mimicIndex = list.findIndex(m => m.id === mimicId);

                if (mimicIndex !== -1) {
                    list.splice(mimicIndex, 1);
                    rootList[folderIndex].folder.expanded = true;
                }
            }
        } else {
            const rootIndex = rootList.findIndex(
                item => item.type === RootItemType.mimic && item?.mimic?.id === mimicId
            );

            if (rootIndex !== -1) {
                rootList = [...rootList.slice(0, rootIndex), ...rootList.slice(rootIndex + 1, rootList.length)];
            } else {
                for (const item of rootList) {
                    if (item.type !== RootItemType.folder) continue;
                    const list = item.folder?.mimicList ?? [];
                    const idx = list.findIndex(m => m.id === mimicId);
                    if (idx !== -1) {
                        list.splice(idx, 1);
                        item.folder.expanded = true;
                        break;
                    }
                }
            }
        }

        let currentMimic: Mimic = undefined;

        if (selectedMimic?.id === mimicId) {
            const { nextMimic, rootList: updatedRootList } = this.findCurrentMimic(rootList);
            currentMimic = nextMimic;
            rootList = updatedRootList;
        }

        return { rootList: [...rootList], currentMimic };
    }

    private applyExpandedState(updatedRootList: RootItem[], prevRootList: RootItem[]) {
        const expandedMap = new Map<string, boolean>();

        for (const item of prevRootList ?? []) {
            if (item.type === RootItemType.folder && item.folder?.id) {
                expandedMap.set(item.folder.id, !!item.folder.expanded);
            }
        }

        return (updatedRootList ?? []).map(item => {
            if (item.type !== RootItemType.folder) return item;

            const prevExpanded = expandedMap.get(item.folder.id) ?? false;
            return {
                ...item,
                folder: {
                    ...item.folder,
                    expanded: prevExpanded,
                },
            };
        });
    }

    private findMimicInRootList(rootList: RootItem[], mimicId: string): { nextSelected: Mimic; folderIndex: number } {
        for (const [index, item] of rootList.entries()) {
            if (item.type === RootItemType.mimic && item.mimic?.id === mimicId)
                return { nextSelected: item.mimic, folderIndex: undefined };

            if (item.type === RootItemType.folder) {
                const found = item.folder?.mimicList?.find(m => m.id === mimicId);
                if (found) return { nextSelected: found, folderIndex: index };
            }
        }
        return { nextSelected: undefined, folderIndex: undefined };
    }

    findCurrentMimic(rootList: RootItem[]) {
        let folderIndex = undefined;
        const next =
            rootList.find(x => x.type === RootItemType.mimic)?.mimic ??
            rootList.find((x, index) => {
                if (x.type === RootItemType.folder && x.folder?.mimicList?.length) {
                    folderIndex = index;
                    return true;
                }
                return false;
            })?.folder.mimicList[0];
        if (folderIndex !== undefined) {
            rootList[folderIndex].folder.expanded = true;
        }
        return { nextMimic: next, rootList };
    }
}
